import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Menu, Settings, Bell, ChevronDown, LogOut, User, Moon, Sun, Languages } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useUiStore } from '@/stores/uiStore'
import { SUPPORTED_LANGUAGES } from '@/i18n'
import { AssistantLauncher } from '@/features/assistant'
import { EntitySwitcher } from './EntitySwitcher'
import { GlobalSearch } from './GlobalSearch'
import { cn } from '@/lib/utils'

export interface TopbarProps {
  onToggleSidebar: () => void
  unreadCount?: number
}

/**
 * The top bar: hamburger, the product's name with a personal greeting, global search,
 * settings, a notification bell with an unread badge, and the user menu.
 */
export function Topbar({ onToggleSidebar, unreadCount = 0 }: TopbarProps) {
  const { t, i18n } = useTranslation()
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const navigate = useNavigate()
  const { theme, setTheme } = useUiStore()

  const initials = (user?.fullName ?? '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')

  const primaryRole = user?.roles[0]?.replace(/_/g, ' ') ?? ''

  return (
    <header className="flex h-[var(--spacing-topbar)] shrink-0 items-center gap-3 border-b border-[var(--color-border)] bg-[var(--color-card)] px-4 lg:px-6">
      <button
        type="button"
        onClick={onToggleSidebar}
        aria-label={t('a11y.toggleSidebar')}
        className="grid size-9 shrink-0 place-items-center rounded-[var(--radius-control)] text-[var(--color-body)] hover:bg-[var(--color-page)]"
      >
        <Menu className="size-5" aria-hidden="true" />
      </button>

      {/*
        The application, not the page. The page renders its own title in its PageHeader, and
        for a while both did: "Directory" appeared twice on the same screen, once as an h1
        that changed nothing and once as the heading that actually belongs to the content.
      */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-lg font-semibold leading-tight text-[var(--color-heading)]">
          {t('app.name')}
        </p>
        {user && (
          <p className="truncate text-xs text-[var(--color-muted)]">
            {t('auth.welcomeBack', { name: user.fullName.split(' ')[0] })}
          </p>
        )}
      </div>

      {/* Global search — people, straight to the profile. */}
      <GlobalSearch />

      {/* Renders nothing for a user who holds one entity, which is most of them. */}
      <div className="hidden shrink-0 md:block">
        <EntitySwitcher />
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {/* The assistant. Renders nothing without assistant:use. */}
        <AssistantLauncher />

        {/* Language */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              aria-label="Language"
              className="grid size-9 place-items-center rounded-full text-[var(--color-body)] hover:bg-[var(--color-page)]"
            >
              <Languages className="size-5" aria-hidden="true" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="z-50 min-w-[150px] rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-card)] p-1 shadow-[var(--shadow-popover)]"
            >
              {SUPPORTED_LANGUAGES.map((language) => (
                <DropdownMenu.Item
                  key={language.code}
                  onSelect={() => void i18n.changeLanguage(language.code)}
                  className={cn(
                    'cursor-pointer rounded px-2.5 py-2 text-sm outline-none hover:bg-[var(--color-page)]',
                    i18n.language === language.code && 'text-[var(--color-primary)]',
                  )}
                >
                  {language.label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        {/* Theme */}
        <button
          type="button"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          aria-label="Toggle dark mode"
          className="grid size-9 place-items-center rounded-full text-[var(--color-body)] hover:bg-[var(--color-page)]"
        >
          {theme === 'dark' ? <Sun className="size-5" aria-hidden="true" /> : <Moon className="size-5" aria-hidden="true" />}
        </button>

        <button
          type="button"
          aria-label={t('a11y.settings')}
          className="grid size-9 place-items-center rounded-full text-[var(--color-body)] hover:bg-[var(--color-page)]"
        >
          <Settings className="size-5" aria-hidden="true" />
        </button>

        {/* Notification bell with unread badge */}
        <button
          type="button"
          aria-label={t('a11y.notifications', { count: unreadCount })}
          className="relative grid size-9 place-items-center rounded-full text-[var(--color-body)] hover:bg-[var(--color-page)]"
        >
          <Bell className="size-5" aria-hidden="true" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 end-0 grid min-w-[18px] place-items-center rounded-full bg-[var(--color-primary)] px-1 text-[10px] font-semibold leading-[18px] text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {/* User menu */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              aria-label={t('a11y.userMenu')}
              className="ms-1 flex items-center gap-2 rounded-full py-1 pe-2 ps-1 hover:bg-[var(--color-page)]"
            >
              <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[var(--color-primary-soft)] text-xs font-semibold text-[var(--color-primary)]">
                {initials || <User className="size-4" aria-hidden="true" />}
              </span>
              <span className="hidden min-w-0 text-start md:block">
                <span className="block truncate text-sm font-medium leading-tight text-[var(--color-heading)]">
                  {user?.fullName}
                </span>
                <span className="block truncate text-[11px] capitalize leading-tight text-[var(--color-muted)]">
                  {primaryRole.toLowerCase()}
                </span>
              </span>
              <ChevronDown className="size-4 shrink-0 text-[var(--color-muted)]" aria-hidden="true" />
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="z-50 min-w-[200px] rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-card)] p-1 shadow-[var(--shadow-popover)]"
            >
              <div className="border-b border-[var(--color-border)] px-2.5 py-2">
                <p className="truncate text-sm font-medium text-[var(--color-heading)]">{user?.fullName}</p>
                <p className="truncate text-xs text-[var(--color-muted)]">{user?.email}</p>
              </div>
              <DropdownMenu.Item
                onSelect={() => void navigate('/profile')}
                className="mt-1 cursor-pointer rounded px-2.5 py-2 text-sm outline-none hover:bg-[var(--color-page)]"
              >
                <User className="me-2 inline size-4" aria-hidden="true" />
                My Profile
              </DropdownMenu.Item>
              <DropdownMenu.Item
                onSelect={() => void logout()}
                className="cursor-pointer rounded px-2.5 py-2 text-sm text-[var(--color-danger)] outline-none hover:bg-[var(--color-tint-red)]"
              >
                <LogOut className="me-2 inline size-4 rtl-flip" aria-hidden="true" />
                {t('auth.signOut')}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  )
}
